import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, Columns3, GanttChartSquare, ListTodo, Plus } from 'lucide-react';
import { tasksAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { t } from '../../lib/i18n';
import { useProject } from '../../layout/ProjectLayout';
import KanbanBoard from '../../components/KanbanBoard';
import MonthGrid from '../../components/MonthGrid';
import TaskListView from '../../components/TaskListView';
import TaskDetailPanel from '../../components/TaskDetailPanel';
import TimelineView from '../../components/TimelineView';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';

export default function TasksScreen() {
  const { project, roster } = useProject();
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [view, setView] = useState(project.settings?.default_view || 'kanban');
  const [assignee, setAssignee] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [groupBy, setGroupBy] = useState('none'); // List view: flat by default, or group by status
  const [selectedId, setSelectedId] = useState(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getUser();
  const readOnly = user?.role === 'guest';

  // deep link: /projects/{id}?task={taskId} opens the panel (My Work / Calendar)
  useEffect(() => {
    const tid = searchParams.get('task');
    if (tid) {
      setSelectedId(tid);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const load = useCallback(async () => {
    const [g, t, l] = await Promise.all([
      tasksAPI.groups(project.id),
      tasksAPI.list(project.id),
      tasksAPI.labels(project.id),
    ]);
    setGroups(g.data.groups);
    setTasks(t.data.tasks);
    setLabels(l.data.labels);
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      tasks.filter(
        (t) =>
          (!assignee || (t.assignee_mids || []).includes(assignee)) &&
          (!labelFilter || (t.labels || []).includes(labelFilter))
      ),
    [tasks, assignee, labelFilter]
  );

  const patchLocal = (taskId, fields) =>
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...fields } : t)));

  const createTask = async (groupId, title) => {
    const { data } = await tasksAPI.create(project.id, { title, group_id: groupId });
    setTasks((prev) => [...prev, data]);
  };

  const moveTask = async (taskId, groupId, order) => {
    const target = groups.find((g) => g.id === groupId);
    const src = tasks.find((t) => t.id === taskId);
    const fields = { group_id: groupId, order };
    // mirror the backend: landing in a done column completes; leaving it reopens
    if (target?.is_done_group && !src?.completed_at) {
      fields.completed_at = new Date().toISOString();
    } else if (target && !target.is_done_group && src?.completed_at) {
      const oldG = groups.find((g) => g.id === src.group_id);
      if (oldG?.is_done_group) fields.completed_at = null;
    }
    patchLocal(taskId, fields);
    try {
      await tasksAPI.move(taskId, groupId, order);
    } catch {
      await load();
    }
  };

  // List view (flat): drag reorders within the linear list; the group is unchanged
  const reorderTask = async (taskId, order) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    patchLocal(taskId, { order });
    try {
      await tasksAPI.move(taskId, task.group_id, order);
    } catch {
      await load();
    }
  };

  // Board: drag a column header to reorder the groups (item 1 — same as the CMS)
  const reorderGroups = async (ids) => {
    setGroups((prev) => ids.map((id) => prev.find((g) => g.id === id)).filter(Boolean));
    try {
      await tasksAPI.reorderGroups(project.id, ids);
    } catch {
      await load();
    }
  };

  const toggleComplete = async (task) => {
    const done = Boolean(task.completed_at);
    patchLocal(task.id, { completed_at: done ? null : new Date().toISOString() });
    try {
      if (done) await tasksAPI.reopen(task.id);
      else await tasksAPI.complete(task.id);
    } catch {
      await load();
    }
  };

  const addGroup = async (e) => {
    e.preventDefault();
    await tasksAPI.createGroup(project.id, { name: groupName });
    setGroupName('');
    setAddingGroup(false);
    await load();
  };

  const toggleSelect = (taskId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });

  const bulk = async (action, extra = {}) => {
    await tasksAPI.bulk([...selected], action, extra);
    setSelected(new Set());
    await load();
  };

  const viewProps = {
    groups,
    tasks: filtered,
    roster,
    labels,
    readOnly,
    onOpen: setSelectedId,
    onCreate: createTask,
    onMove: moveTask,
    onReorderGroups: reorderGroups,
    onToggleComplete: toggleComplete,
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-md border border-pm-border">
          {[
            { id: 'kanban', icon: Columns3, label: 'Board' },
            { id: 'list', icon: ListTodo, label: 'List' },
            { id: 'timeline', icon: GanttChartSquare, label: 'Timeline' },
            { id: 'calendar', icon: CalendarDays, label: 'Calendar' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs ${
                view === id ? 'bg-pm-raised text-pm-text' : 'text-pm-muted hover:text-pm-text'
              }`}
            >
              <Icon size={13} />
              {t(label)}
            </button>
          ))}
        </div>
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className={`${inputCls} w-auto py-1.5 text-xs`}>
          <option value="">{t('All assignees')}</option>
          {roster.map((m) => (
            <option key={m.mid} value={m.mid}>
              {m.name}
            </option>
          ))}
        </select>
        <select
          value={labelFilter}
          onChange={(e) => setLabelFilter(e.target.value)}
          className={`${inputCls} w-auto py-1.5 text-xs`}
        >
          <option value="">{t('All labels')}</option>
          {labels.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        {view === 'list' && (
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className={`${inputCls} w-auto py-1.5 text-xs`}
            title={t('Group by')}
          >
            <option value="none">{t('No grouping')}</option>
            <option value="status">{t('Group by status')}</option>
          </select>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={() => setAddingGroup(true)}
            className={`${btnGhost} ml-auto inline-flex items-center gap-1.5 py-1.5 text-xs`}
          >
            <Plus size={13} />
            {t('Add group')}
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1">
        {view === 'kanban' && <KanbanBoard {...viewProps} />}
        {view === 'list' && (
          <TaskListView
            {...viewProps}
            groupBy={groupBy}
            onReorder={reorderTask}
            selected={selected}
            onToggleSelect={toggleSelect}
          />
        )}
        {view === 'timeline' && (
          <TimelineView groups={groups} tasks={filtered} readOnly={readOnly} onOpen={setSelectedId} onChanged={load} />
        )}
        {view === 'calendar' && (
          <MonthGrid
            items={filtered
              .filter((t) => t.due_date || t.start_date)
              .map((t) => {
                const g = groups.find((x) => x.id === t.group_id);
                return {
                  id: t.id,
                  title: t.title,
                  date: t.due_date || t.start_date,
                  color: g?.color || 'var(--pm-faint)',
                  done: Boolean(t.completed_at),
                  readOnly,
                };
              })}
            onItemClick={(it) => setSelectedId(it.id)}
            draggable={!readOnly}
            onItemDrop={async (taskId, iso) => {
              patchLocal(taskId, { due_date: iso });
              try {
                await tasksAPI.patch(taskId, { due_date: iso });
              } finally {
                await load();
              }
            }}
          />
        )}
      </div>

      {selected.size > 0 && view === 'list' && !readOnly && (
        <div className="pointer-events-auto fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-pm-border bg-pm-raised px-3 py-2 shadow-xl">
          <span className="text-xs text-pm-muted">{selected.size} {t('selected')}</span>
          <button type="button" onClick={() => bulk('complete')} className={`${btnGhost} py-1 text-xs`}>
            {t('Complete')}
          </button>
          <select
            defaultValue=""
            onChange={(e) => e.target.value && bulk('move', { group_id: e.target.value })}
            className={`${inputCls} w-auto py-1 text-xs`}
          >
            <option value="" disabled>
              {t('Move to…')}
            </option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => window.confirm(`Delete ${selected.size} task(s)?`) && bulk('delete')}
            className={`${btnGhost} py-1 text-xs`}
            style={{ color: 'var(--pm-danger)' }}
          >
            {t('Delete')}
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-pm-faint hover:text-pm-text">
            {t('Clear')}
          </button>
        </div>
      )}

      {selectedId && (
        <TaskDetailPanel
          taskId={selectedId}
          project={project}
          roster={roster}
          labels={labels}
          readOnly={readOnly}
          onClose={() => setSelectedId(null)}
          onChanged={load}
          onLabelsChanged={async () => {
            const { data } = await tasksAPI.labels(project.id);
            setLabels(data.labels);
          }}
        />
      )}

      {addingGroup && (
        <Modal title={t('Add task group')} onClose={() => setAddingGroup(false)}>
          <form onSubmit={addGroup} className="space-y-4">
            <Field label={t('Group name')}>
              <input value={groupName} onChange={(e) => setGroupName(e.target.value)} required autoFocus className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAddingGroup(false)} className={btnGhost}>
                {t('Cancel')}
              </button>
              <button type="submit" disabled={!groupName.trim()} className={btnPrimary}>
                {t('Add group')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
